import { FC, useRef, useState, useEffect, useMemo, useContext } from 'react'
import get from 'lodash/get'
import { MediaPermissionsErrorType, requestMediaPermissions } from 'mic-check'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import * as Proto from 'types/twoFactorProto'

import LiveKitAvatar, {
  ILiveKitAvatar
} from 'shared/components/LiveKitAvatar'

import Recorder from 'shared/components/Recorder'
import PageContainer from 'shared/components/PageContainer'
import { SessionLatencies } from 'types/internal'
import { TwoFactorT } from 'types/twoFactor'
import ControlPanel, {
  IControlPanel,
  Step
} from 'shared/components/ControlPanel'
import FixPermission from 'shared/components/FixPermission'
import PermissionsHandler from 'shared/components/PermissionsHandler'
import PACKAGE from '../../package.json'
import map from 'lodash/map'
import compact from 'lodash/compact'
import jsLogger from 'js-logger'
import { getBackClient } from 'shared/controllers/back'
import { AvatarSessionBootstrap } from 'types/back'
import EmailInput from 'pages/converstion/EmailInput'
import NetworkSpeedCheck from 'shared/components/NetworkSpeedCheck'
import UserClickContext from 'shared/contexts/UserClickContext'
import StreamContext from 'shared/contexts/StreamContext'
import ServerTranscriber from 'shared/components/ServerTranscriber'
import LocalizationContext from 'shared/contexts/LocalizationContext'
import ConnnectionContext from 'shared/contexts/ConnectionContext'
import ConnectionStatus from 'shared/components/ConnectionStatus'

// Demo2 is fully server-authoritative: the socket server owns STT, turn-taking,
// barge-in, the silence timer, phrase generation, avatar provisioning + actuation
// and the session lifecycle. This page is a THIN client — it renders the avatar,
// streams the mic to the server for STT, publishes the camera+mic into the
// LiveKit room (server-side Egress records), reflects state the server relays
// (serverPhrase / requestEmailInput / sessionFinished), and drives the UI. It
// does NOT run any client-side conversation control, and it does not track the
// avatar's speaking state — that lives entirely on the server.

type Props = {
  conversationInfo: Proto.ConversationStartResponse
  onConversationFinished: () => void
  initVersion: 0 | 1
  isTest: boolean
  lang: string
  onChangeLang: (lang: string) => void
  onClosed: () => void
  onError?: (er: string) => void
}

const Conversation: FC<Props> = ({
  conversationInfo,
  onConversationFinished,
  initVersion,
  isTest,
  lang,
  onChangeLang,
  onClosed,
  onError
}) => {
  const {
    interactionId,
    availableLanguages,
    avatarImageUrl,
    interruptionMode
  } = conversationInfo
  const { getString } = useContext(LocalizationContext)
  const { updateConnectionStatus } = useContext(ConnnectionContext)
  const { setClicked } = useContext(UserClickContext)
  const { stream, startStream } = useContext(StreamContext)
  const [startButtonClicked, setStartButtonClicked] = useState(false)
  const avatarRef = useRef<ILiveKitAvatar>(null)
  const [avatarInitialized, setAvatarInitialized] = useState(false)
  // One-shot guard: the greeting is cued to the server exactly once, when the
  // avatar is ready and the user has interacted.
  const greetingCuedRef = useRef(false)
  const [orientation, setOrientation] = useState<'portrait' | 'landscape' | ''>(
    ''
  )

  const [steps, setSteps] = useState<TwoFactorT.StepT[]>(conversationInfo.steps)

  // Server-minted LiveKit/LiveAvatar creds. Null while provisioning is in flight;
  // null after `avatarProvisionDone` means provisioning failed — there is no
  // client-side fallback path, so the avatar stays on its still photo.
  const [avatarSession, setAvatarSession] =
    useState<AvatarSessionBootstrap | null>(null)
  const [avatarProvisionDone, setAvatarProvisionDone] = useState(false)
  const avatarSessionRef = useRef<AvatarSessionBootstrap | null>(null)

  const [permissionsError, setPermissionsError] =
    useState<MediaPermissionsErrorType | null>(null)
  const controlPanelRef = useRef<IControlPanel>(null)
  const [userName, setUserName] = useState<string | null>(null)
  const [checkingPermissions, setCheckingPermissions] = useState(false)
  const [controlPanelVisible, setControlPanelVisible] = useState(
    initVersion === 1
  )
  const [permisssionsGranted, setPermissionsGranted] = useState(false)

  const [showEmailInput, _setShowEmailInput] = useState(false)
  const [subtitlesOpenedByDefault, setSubtitlesOpenedByDefault] =
    useState(false)
  const cameraContainerRef = useRef<HTMLDivElement>(null)

  const processUrlParams = () => {
    const url = new URL(window.location.href)
    const cc = url.searchParams.get('cc')
    if (cc !== null) {
      setSubtitlesOpenedByDefault(true)
    }
  }

  useEffect(() => {
    jsLogger.info('CONVERSATION START')

    const back = getBackClient()
    back.connectionStatusHandler = (isConnected: boolean) =>
      updateConnectionStatus('back', isConnected)

    // Server-side STT: drive the subtitle UI off the server's interim transcript.
    // The final transcript / turn are owned by the server (serverPhrase), so the
    // client only mirrors the interim for display.
    back.sttInterimHandler = (transcript: string) => onSpeech(transcript)

    // The server drives the whole turn (trigger, buffer, silence, barge-in) and
    // pushes the generated phrase (serverPhrase) + the authoritative step list.
    back.serverPhraseHandler = (response) =>
      onNewAppState(response as unknown as Proto.SubmitReplyResponse, 0)
    // UI-command channel: the server tells us when to show the email UI.
    back.requestEmailInputHandler = () => setShowEmailInput(true)
    back.emitTurnControl('server', interruptionMode)

    // Explicit end-of-session signal from the server (fires when the final isOver
    // phrase finishes) → go straight to recap. This is the sole recap trigger:
    // the avatar's speaking lifecycle is owned by the server and not mirrored
    // client-side anymore.
    back.sessionFinishedHandler = () => {
      jsLogger.log('Conversation: sessionFinished → recap')
      onConversationFinished()
    }
    // Ask the server to mint the LiveKit room + LiveAvatar LITE session and hand
    // back the browser bootstrap ({livekitUrl, roomName, clientToken}); the mount
    // is gated on it (see renderAvatar).
    back
      .provisionAvatar()
      .then((session) => {
        avatarSessionRef.current = session
        setAvatarSession(session)
      })
      .catch(() => {
        avatarSessionRef.current = null
        setAvatarSession(null)
      })
      .finally(() => setAvatarProvisionDone(true))

    processUrlParams()

    // @ts-ignore
    if (window.fireTestEvent) {
      // @ts-ignore
      window.fireTestEvent('ev_conversation_started', {
        interactionId,
        version: PACKAGE.version
      })
    }
    function handleOrientationChange(event: { matches: any; media: any }) {
      const { matches } = event
      setOrientation(matches ? 'portrait' : 'landscape')
    }

    const mediaQueryPortrait = window.matchMedia('(orientation: portrait)')
    setOrientation(mediaQueryPortrait.matches ? 'portrait' : 'landscape')

    mediaQueryPortrait.addEventListener('change', handleOrientationChange)

    return () => {
      mediaQueryPortrait.removeEventListener('change', handleOrientationChange)
      const back = getBackClient()
      back.disconnect()
    }
  }, [])

  useEffect(() => {
    jsLogger.debug('useEffect triggered for initial phrase scheduling', {
      startButtonClicked,
      streamAvailable: !!stream,
      greetingCued: greetingCuedRef.current,
      avatarInitialized
    })

    if (
      avatarInitialized &&
      startButtonClicked &&
      stream &&
      !greetingCuedRef.current
    ) {
      const q = get(steps, 0)
      jsLogger.debug('Checking first step for initial phrase', { step: q })

      if (q.sType === TwoFactorT.StepTypeT.QUESTION && stream) {
        // The avatar is now playing and the user has interacted, so audio playout
        // is unlocked — cue the server to voice the greeting over the LITE control
        // WS. Fire exactly once.
        greetingCuedRef.current = true
        jsLogger.log('onAvatarStarted: cueing server greeting', q)
        getBackClient().emitAvatarReady()
      }
    }
  }, [startButtonClicked, stream, avatarInitialized, steps])

  const setShowEmailInput = (v: boolean) => {
    _setShowEmailInput(v)
  }

  const controlPanelSteps = useMemo(() => {
    return compact(
      map(steps, (s) => {
        if (
          s.sType === TwoFactorT.StepTypeT.QUESTION ||
          s.sType === TwoFactorT.StepTypeT.REPLY
        ) {
          return {
            id: s.id,
            text: s.text,
            isAvatar: s.sType === TwoFactorT.StepTypeT.QUESTION
          } as Step
        } else {
          return null
        }
      })
    )
  }, [steps])

  const onAvatarStarted = () => {
    jsLogger.debug('avatar started')
    setAvatarInitialized(true)
    // @ts-ignore
    if (window.fireTestEvent) {
      // @ts-ignore
      window.fireTestEvent('ev_heygen_started', {
        interactionId,
        version: PACKAGE.version
      })
    }
  }

  // Server-turn app-state sync: the server pushes each generated phrase +
  // authoritative step list (serverPhrase). We only reflect it in the UI and
  // track the end-of-session marker; the avatar is voiced server-side.
  const onNewAppState = async (
    serverResponse: Proto.SubmitReplyResponse | Proto.Error,
    latency: number
  ) => {
    jsLogger.log('onNewAppState: serverResponse', { serverResponse })
    const er = get(serverResponse, 'error')
    if (er) {
      onError && onError(er)
      return
    }
    const res = serverResponse as Proto.SubmitReplyResponse
    if (!res) {
      jsLogger.log('serverPhrase has no value, exit function', res)
      return
    }
    const q = res.q
    jsLogger.log('Next question', { text: q?.text, over: res.isOver })

    if (!q) return

    if (q.latencies) {
      q.latencies.frontendReplyGenerationLatency = latency
    }
    // Keep the transcript panel in sync — the server ships the authoritative step
    // list on the serverPhrase payload. End-of-session (isOver) is owned by the
    // server, which signals recap explicitly via sessionFinished.
    const newSteps = get(res, 'steps') as TwoFactorT.StepT[] | undefined
    if (newSteps && newSteps.length) {
      setSteps(newSteps)
    }
    if (res.userName && userName !== res.userName) {
      setUserName(res.userName)
    }
  }

  // Server-side STT: mirror the server's interim transcript into the subtitle UI.
  const onSpeech = (v: string) => {
    controlPanelRef.current?.setSpeech(`${v}`)
  }

  const onHGLatencies = (
    full: number,
    streamingNew: number,
    connectionPreparing: number,
    websocket: number
  ) => {
    const latencies: Partial<SessionLatencies> = {
      heyGenInitialization: full,
      heyGenStreamingNew: streamingNew,
      heyGenConnectionPreparing: connectionPreparing,
      heyGenWebsocket: websocket
    }
    getBackClient()
      .demo2SendLatencies(latencies)
      .catch((e) => jsLogger.warn('demo2SendLatencies failed', e))
  }

  const handleClose = () => {
    const back = getBackClient()
    back.disconnect()
    onClosed()
  }

  const handleVideoClick = () => {
    if (
      orientation === 'portrait' &&
      !permissionsError &&
      permisssionsGranted
    ) {
      controlPanelRef?.current?.click()
    }
  }

  const onTranscriberConnected = (latency: number, connectionNum: number) => {
    const latencies: Partial<SessionLatencies> = {}
    if (connectionNum === 1) {
      latencies.transcriberConnection = latency
      latencies.transcriberConnectionsAmount = 1
    } else {
      latencies.transcriberConnectionsAmount = connectionNum
    }
    getBackClient()
      .demo2SendLatencies(latencies)
      .catch((e) => jsLogger.warn('demo2SendLatencies failed', e))
  }

  if (permissionsError) {
    return <PermissionsHandler permissionsError={permissionsError} />
  }

  const avatarEnabled = true

  const renderButton = () => {
    const title = permisssionsGranted
      ? getString('start', lang)
      : getString('request_permissions', lang)
    const onClick = permisssionsGranted
      ? () => {
          setStartButtonClicked(true)
          setControlPanelVisible(true)
        }
      : requestPermissionsInternally
    return (
      <button
        className='h-16 bg-blue-400 text-white font-medium text-[16px] w-[260px] mt-7 rounded-[4px]'
        onClick={() => onClick()}
      >
        {title}
      </button>
    )
  }

  const onHGConnectionStatusChanged = (isConnected: boolean) => {
    updateConnectionStatus('HG', isConnected)
  }

  const onDGConnectionStatusChanged = (isConnected: boolean) => {
    updateConnectionStatus('DG', isConnected)
  }

  const requestPermissionsInternally = async () => {
    jsLogger.log('requestPermissionsInternally')
    try {
      startStream()
      setCheckingPermissions(true)
      jsLogger.debug('permissions_requested internally')
      await requestMediaPermissions()
      jsLogger.debug('permissions_granted internally')
      setPermissionsError(null)
      setPermissionsGranted(true)
      setClicked(true)
      setCheckingPermissions(false)
    } catch (error: any) {
      setCheckingPermissions(false)
      jsLogger.error('MediaOnboardingDialog: ', error)
      if (error.type === MediaPermissionsErrorType.Generic) {
        setPermissionsError(MediaPermissionsErrorType.SystemPermissionDenied)
      } else if (
        error.type === MediaPermissionsErrorType.SystemPermissionDenied
      ) {
        // user denied permission
        setPermissionsError(MediaPermissionsErrorType.SystemPermissionDenied)
      } else if (
        error.type === MediaPermissionsErrorType.UserPermissionDenied
      ) {
        // browser doesn't have access to devices
        setPermissionsError(MediaPermissionsErrorType.UserPermissionDenied)
      } else if (
        error.type === MediaPermissionsErrorType.CouldNotStartVideoSource
      ) {
        // most likely when other apps or tabs are using the cam/mic (mostly windows)
        setPermissionsError(MediaPermissionsErrorType.CouldNotStartVideoSource)
      }
    }
  }

  const renderAvatar = () => {
    // Wait for provisioning to resolve before mounting. While we wait — and if it
    // fails (no client fallback) — show the avatar's still photo (same container/
    // layout as LiveKitAvatar) instead of a blank white screen.
    if (!avatarProvisionDone || !avatarSession) {
      return (
        <div className='h-full w-full flex flex-col justify-between flex-1 relative bg-gray-700'>
          {avatarImageUrl && (
            <img
              src={avatarImageUrl}
              alt='Avatar'
              className='absolute w-full h-full object-cover left-0 top-0 right-0 bottom-0'
            />
          )}
        </div>
      )
    }
    return (
      <LiveKitAvatar
        ref={avatarRef}
        avatarSession={avatarSession}
        userStream={stream}
        onSessionStarted={onAvatarStarted}
        handleVideoClick={handleVideoClick}
        avatarImageUrl={avatarImageUrl}
        onError={onError}
        trackInitializationLatencies={onHGLatencies}
        onConnectionStatusChanged={onHGConnectionStatusChanged}
      />
    )
  }

  const renderControlPanel = () => {
    if (controlPanelVisible) {
      return (
        <ControlPanel
          ref={controlPanelRef}
          permissionsError={permissionsError}
          handleClose={handleClose}
          permissionsChecked={permisssionsGranted}
          steps={controlPanelSteps}
          userName={userName}
          isRecording={true}
          orientation={orientation}
          availableLanguages={availableLanguages}
          lang={lang}
          onChangeLang={onChangeLang}
          subtitlesOpenedByDefault={subtitlesOpenedByDefault}
        />
      )
    }
  }

  const onNotNowClick = async () => {
    setShowEmailInput(false)
    try {
      await getBackClient().demo2SkipEmail()
    } catch (e) {
      jsLogger.warn('demo2SkipEmail failed', e)
    }
  }

  const onConfirmClick = async (email: string) => {
    jsLogger.log('onConfirmClick', { email })
    setShowEmailInput(false)
    try {
      await getBackClient().demo2SubmitEmail(email)
    } catch (e) {
      jsLogger.warn('demo2SubmitEmail failed', e)
    }
  }

  const renderEmailInput = () => {
    if (showEmailInput) {
      return (
        <EmailInput
          onNotNowClick={onNotNowClick}
          onConfirmClick={onConfirmClick}
          locale={lang}
        />
      )
    }
  }

  return (
    <PageContainer version={PACKAGE.version}>
      {avatarEnabled && renderAvatar()}
      {permisssionsGranted && (
        <div
          ref={cameraContainerRef}
          className={
            'flex-1 bg-slate-100 rounded-md absolute top-[3rem] left-5 w-28 h-44 transition-all duration-500 ease-in-out'
          }
        >
          <Recorder onError={onError} />
        </div>
      )}
      {controlPanelVisible && !isTest && (
        <ServerTranscriber
          onRawAudio={(pcm, sampleRate) =>
            getBackClient().emitAudioChunk(pcm, sampleRate)
          }
          onConnected={onTranscriberConnected}
          onConnectionStatusChanged={onDGConnectionStatusChanged}
        />
      )}

      {permissionsError === MediaPermissionsErrorType.UserPermissionDenied && (
        <FixPermission />
      )}
      {renderControlPanel()}
      {!controlPanelVisible && initVersion === 0 && (
        <div className='absolute top-0 left-0 bottom-0 right-0 w-full h-full flex items-center justify-center'>
          <div className='z-10 absolute top-20 left-4 text-gray-300'>
            <div>
              <div style={{ height: '50px', width: '50px' }}>
                <DotLottieReact
                  src='https://lottie.host/1ba91606-15d0-4f27-9f17-095c58dfb5ac/IivUjwcaOY.json'
                  autoplay
                  loop
                  backgroundColor='transparent'
                  speed={1}
                  style={{ width: '100%', height: '100%' }}
                  mode='forward'
                />
              </div>
            </div>
            <span className='text-[10px] pt-1'>
              <i>{getString('conversation_volume_up', lang)}</i>
            </span>
          </div>
          {checkingPermissions && (
            <div className='z-10 text-[24px] text-white font-semibold flex flex-col items-center'>
              <span className='drop-shadow-[1px_1px_1px_rgba(0,0,0,1)]'>
                <i>{getString('conversation_initializing', lang)}</i>
              </span>
              <div className='h-1 w-[187px] bg-white mt-[23px]'>
                <div className='h-1 w-[106px] bg-teal-600' />
              </div>
            </div>
          )}

          {!checkingPermissions && !startButtonClicked && (
            <div className='z-10 w-[300px] flex flex-col items-center'>
              <span className='font-semibold text-[24px] text-white drop-shadow-[1px_1px_1px_rgba(0,0,0,1)] text-center leading-8'>
                {getString('conversation_about_to_chat', lang)}
              </span>
              <div className='w-full bg-white mt-10 rounded-xl flex flex-col items-center pt-7 pb-4'>
                <span className='text-[20px] font-semibold text-center leading-7 px-4'>
                  {permisssionsGranted
                    ? getString('lets_verity_you', lang)
                    : getString('conversation_request_permissions', lang)}
                </span>
                {!startButtonClicked && renderButton()}
              </div>
            </div>
          )}
        </div>
      )}
      <NetworkSpeedCheck />
      {renderEmailInput()}
      <ConnectionStatus />
    </PageContainer>
  )
}

export default Conversation
